import { usePage } from "@/components/page/PageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { components } from "@/lib/openapi";
import { AirportElement } from "@/lib/types";
import {
  Card,
  ExploreDisplay,
  MultiSeriesChart,
  SingleSeriesChart,
} from "@/pages/data/DataChart";
import {
  useTerminal,
  useTerminalId,
} from "@/pages/data/terminal/terminal-utils";
import {
  RestroomInfo,
  TrafficChart,
} from "@/pages/data/terminal/TerminalCharts";
import { getAggregateData } from "@/queries/report";
import { useReportContext } from "@/stores/ReportContext";
import React, { useEffect } from "react";

export type CheckpointSchema = components["schemas"]["CheckpointSchema"];
export type SecurityLaneSchema = components["schemas"]["SecurityLaneSchema"];

export function TerminalGatesPage() {
  const { setTitles } = usePage();
  const { reportId } = useReportContext();
  const terminalId = useTerminalId();
  const [direction, setDirection] = React.useState<"dep" | "arr">("dep");
  const [passengerType, setPassengerType] = React.useState<
    "direct" | "connecting" | "both"
  >("both");

  useEffect(() => {
    setTitles(["Gate Lounge Overview"]);
  }, []);

  if (!terminalId) {
    return null;
  }

  return (
    <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
      <div className="col-span-full h-auto md:h-90 ">
        <Card
          title="Passenger Density"
          innerClassName="max-w-full"
          actions={
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground">
                  Direction
                </label>
                <Select
                  value={direction}
                  onValueChange={(value) =>
                    setDirection(value as "dep" | "arr")
                  }
                >
                  <SelectTrigger className="w-[140px] sm:w-[180px] text-sm">
                    <SelectValue placeholder="Select direction" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dep">Departure</SelectItem>
                    <SelectItem value="arr">Arrival</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground">
                  Passenger Type
                </label>
                <Select
                  value={passengerType}
                  onValueChange={(value) =>
                    setPassengerType(value as "direct" | "connecting" | "both")
                  }
                >
                  <SelectTrigger className="w-[140px] sm:w-[180px] text-sm">
                    <SelectValue placeholder="Select passenger type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direct">Direct</SelectItem>
                    <SelectItem value="connecting">Connecting</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          }
        >
          <ExploreDisplay
            height={1100}
            query={{
              terminal_id: terminalId.toString(),
              measure: "pax",
              direction: direction,
              type: "gate",
              pax_type: passengerType === "both" ? null : passengerType,
            }}
          />
        </Card>
      </div>
    </div>
  );
}

function CheckpointInfo({ checkpoint }: { checkpoint: CheckpointSchema }) {
  const { reportId } = useReportContext();

  const { data: trafficData, isSuccess } = getAggregateData(
    [
      {
        query: {
          type: "checkpoint",
          measure: "pax",
          checkpoint_id: checkpoint.id,
        },
      },
    ],
    reportId
  );

  const laneQueries = checkpoint.lanes.map((lane: SecurityLaneSchema) => ({
    query: {
      type: "security_lane",
      measure: "pax",
      security_lane_id: lane.id,
    },
  }));

  const { data: trafficDataByType, isSuccess: isSuccessByType } =
    getAggregateData(laneQueries, reportId);

  if (!isSuccess || !trafficData || !isSuccessByType || !trafficDataByType)
    return null;

  if (trafficData.length === 0 || trafficDataByType.length === 0) return null;

  // Check if all values are 0
  const allZeros = trafficData[0].values.every((v) => v === 0);
  if (allZeros) return null;

  return (
    <div className="col-span-full sm:col-span-2">
      <div className="mb-2 text-lg border-b border-slate-600 w-full py-4 mx-2">
        Checkpoint {checkpoint.name}
      </div>
      <div className="w-full h-auto md:h-90 flex">
        <div className="w-full h-auto md:h-90 p-2">
          <Card title="Passenger Volume">
            <SingleSeriesChart
              type="column"
              values={trafficData[0].values}
              labels={trafficData[0].labels}
              xAxisTitle="Time"
              yAxisTitle="Passengers"
              colorIndex={0}
            />
          </Card>
        </div>
      </div>
      <div className="w-full h-auto md:h-90 flex">
        <div className="w-full h-auto md:h-90 p-2">
          <Card title="Passenger Volume By Lanes">
            <MultiSeriesChart
              series={trafficDataByType.map((d, i) => ({
                name: checkpoint.lanes[i].name,
                values: d.values,
              }))}
              labels={trafficDataByType[0].labels}
              xAxisTitle={"Time"}
              yAxisTitle={"Passengers"}
              totalRequired={true}
              chartType="column"
            />
          </Card>
        </div>
      </div>
    </div>
  );
}

export function TerminalCheckpointsPage() {
  const { setTitles } = usePage();
  const { reportId } = useReportContext();
  const terminal = useTerminal();

  useEffect(() => {
    setTitles(["Checkpoints Overview"]);
  }, []);

  if (!terminal) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
      <div className="col-span-1 sm:col-span-2">
        <Card
          title="Checkpoint - Passenger Density"
          innerClassName="max-w-full"
        >
          <ExploreDisplay
            query={{
              type: "checkpoint",
              terminal_id: terminal.id.toString(),
              measure: "pax",
            }}
          />
        </Card>
      </div>
      {terminal.checkpoints.map((c) => {
        return <CheckpointInfo checkpoint={c} key={c.id} />;
      })}
    </div>
  );
}

function TicketingInfo({ ticketing }: { ticketing: AirportElement }) {
  const { reportId } = useReportContext();

  const { data: trafficData, isSuccess: trafficSuccess } = getAggregateData(
    [
      {
        query: {
          ticketing_id: ticketing.id,
        },
      },
    ],
    reportId,
    true
  );

  // Check if all values are 0
  const allZeros =
    trafficSuccess && trafficData[0].values.every((v) => v === 0);
  if (allZeros) return null;

  return (
    <div className="col-span-full sm:col-span-2">
      <div className="mb-2 text-lg border-b border-slate-600 w-full py-4 mx-2">
        Ticketing Desk {ticketing.name}
      </div>
      <div className="w-full h-auto md:h-90 flex">
        <div className="w-full h-auto md:h-90 p-2">
          <Card title="Passenger Count">
            {trafficSuccess && (
              <SingleSeriesChart
                type={"column"}
                values={trafficData[0].values}
                labels={trafficData[0].labels}
                xAxisTitle={"Time"}
                yAxisTitle={"Passengers"}
              />
            )}
          </Card>
        </div>
        {/*<div className="w-1/2 h-auto md:h-90 p-2">*/}
        {/*  <WaitTimeChart*/}
        {/*    config={{*/}
        {/*      ticketing_id: ticketing.id,*/}
        {/*    }}*/}
        {/*  />*/}
        {/*</div>*/}
      </div>
    </div>
  );
}

export function TerminalTicketingPage() {
  const { setTitles } = usePage();
  const { reportId } = useReportContext();
  const terminal = useTerminal();

  useEffect(() => {
    setTitles(["Ticketing Desks Overview"]);
  }, []);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
      <div className="col-span-1 sm:col-span-2">
        <Card
          title="Ticketing Desk Passenger Density"
          innerClassName="max-w-full"
        >
          <ExploreDisplay
            query={{
              type: "ticketing",
              terminal_id: terminal.id.toString(),
              measure: "pax",
            }}
          />
        </Card>
      </div>
      {terminal.ticketings.map((c) => {
        return <TicketingInfo ticketing={c} key={c.id} />;
      })}
    </div>
  );
}

export function TerminalRestroomPage() {
  const { setTitles } = usePage();
  const { reportId } = useReportContext();
  const terminal = useTerminal();

  useEffect(() => {
    setTitles(["Restrooms Overview"]);
  }, []);

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div className="col-span-1 sm:col-span-2">
          <Card title="Restroom Passenger Density" innerClassName="max-w-full">
            <ExploreDisplay
              height={800}
              query={{
                type: "restroom",
                terminal_id: "" + terminal.id,
                measure: "pax",
              }}
            />
          </Card>
        </div>
        {terminal.restrooms.map((r) => (
          <RestroomInfo key={r.id} restroom={r} />
        ))}
      </div>
    </div>
  );
}

function DoorInfo({ door }: { door: AirportElement }) {
  const { reportId } = useReportContext();
  const { data: trafficData, isSuccess } = getAggregateData(
    [{ query: { door_id: door.id } }],
    reportId,
    true
  );

  if (!isSuccess || !trafficData) return null;

  // Check if all values are 0
  const allZeros = trafficData[0].values.every((v) => v === 0);
  if (allZeros) return null;

  return (
    <Card title={"Door " + door.name}>
      <TrafficChart query={{ door_id: door.id }} />
    </Card>
  );
}

export function TerminalDoorPage() {
  const { setTitles } = usePage();
  const { reportId } = useReportContext();
  const terminal = useTerminal();

  useEffect(() => {
    setTitles(["Curb Overview"]);
  }, []);

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="col-span-1 sm:col-span-2">
          <Card title="Curb - Passenger Density" innerClassName="max-w-full">
            <ExploreDisplay
              height={500}
              query={{
                type: "door",
                terminal_id: "" + terminal.id,
                measure: "pax",
              }}
            />
          </Card>
        </div>
        {terminal.doors.map((d) => (
          <DoorInfo key={d.id} door={d} />
        ))}
      </div>
    </div>
  );
}
